import type {
  AvailabilityBlockType,
  GarmentJobKind,
  GarmentJobStatus,
  GarmentState,
  GarmentTimelineSource,
  UnitStatus,
} from '../generated/enums';
import type { Hue } from '../vocabulary';
import { humanizeEnum } from '../format/text';

/**
 * How a garment's history reads, and what each entry is allowed to say.
 *
 * <p>
 * The pure half of the timeline panel, in the shape `pages/bookings/draft.ts` established. The
 * server returns four kinds of record time-ordered into one list; this decides the sentence, the
 * colour and the icon for each, and the screen holds the query and the markup.
 * </p>
 *
 * <p>
 * <strong>The four sources stay visibly distinct, and that is the design rather than a detail.</strong>
 * `UnitStatus` (ten members), `GarmentState` (eleven) and `GarmentJobStatus` (six) are three state
 * machines that overlap in vocabulary — "Cleaning" is a unit status <em>and</em> a job kind — and
 * flattening them into one invented track produces a history that reads smoothly and is wrong. Each
 * source keeps its own hue and its own phrasing, which is the same argument `StageTracker` makes for
 * six pips rather than ten.
 * </p>
 */

/**
 * Cache key for one garment's history.
 *
 * <p>
 * Here rather than in the component file, so that file exports components and nothing else — the
 * rule Fast Refresh needs, and the same split every other screen makes between its pure module and
 * its markup.
 * </p>
 */
export const garmentTimelineKey = ['item-unit', 'timeline'] as const;

/** One event, exactly as the server sends it. */
export interface TimelineEvent {
  source: GarmentTimelineSource;
  occurredOn: string;
  fromDate: string | null;
  toDate: string | null;
  unitStatus: UnitStatus | null;
  previousUnitStatus: UnitStatus | null;
  garmentState: GarmentState | null;
  jobStatus: GarmentJobStatus | null;
  jobKind: GarmentJobKind | null;
  blockType: AvailabilityBlockType | null;
  isReleased: boolean | null;
  reference: string | null;
  partyName: string | null;
  reason: string | null;
  refId: number;
  byName: string | null;
}

export interface GarmentTimeline {
  unitId: number;
  serialCode: string;
  events: TimelineEvent[];
  bookingsWithheld: boolean;
  jobsWithheld: boolean;
}

/**
 * The colour each track owns.
 *
 * <p>
 * From `components/hues.ts`, and matched to the module each track belongs to so the timeline reads
 * in the same vocabulary as the rest of the product: bookings take Rental's teal, jobs take
 * Wardrobe's purple, and the garment's own status takes the catalogue's orange. The calendar is
 * `secondary`, which nothing else on this panel uses — a block is the one entry that is a *span*
 * rather than a moment, and it earns its own colour by being a different kind of thing.
 * </p>
 *
 * <p>
 * <strong>`None` is in the map because the generated union carries it</strong>, and a lookup that
 * did not cover it would be a compile error the day someone read it. It cannot occur in a response.
 * </p>
 */
export const sourceHue: Record<GarmentTimelineSource, Hue> = {
  None: 'primary',
  Status: 'orange',
  Block: 'secondary',
  Booking: 'primary',
  Job: 'purple',
};

/** What the track is called, where the panel groups or labels by it. */
export const sourceLabel: Record<GarmentTimelineSource, string> = {
  None: 'Other',
  Status: 'Garment',
  Block: 'Calendar',
  Booking: 'Booking',
  Job: 'Job',
};

/**
 * The one line that says what happened, in the words of the track it came from.
 *
 * <p>
 * A function rather than a map, because three of the four need two of the entry's fields to say
 * anything useful — "Available → On rent" is the sentence, and "On rent" alone is a status, not an
 * event.
 * </p>
 */
export function eventTitle(entry: TimelineEvent): string {
  switch (entry.source) {
    case 'Status':
      return entry.previousUnitStatus
        ? `${humanise(entry.previousUnitStatus)} → ${humanise(entry.unitStatus)}`
        : // The first row records the garment entering service. There is no previous state, and
          // inventing one would make intake look like a transition from nothing.
          `Entered service as ${humanise(entry.unitStatus).toLowerCase()}`;

    case 'Block':
      return entry.isReleased
        ? `${humanise(entry.blockType)} — released`
        : `${humanise(entry.blockType)}`;

    case 'Booking':
      return entry.reference
        ? `${entry.reference} · ${humanise(entry.garmentState)}`
        : // An enquiry carries no number at all, deliberately — numbering one leaves a hole in the
          // register for something that never became a booking.
          `Not numbered · ${humanise(entry.garmentState)}`;

    case 'Job':
      return `${humanise(entry.jobKind)} · ${humanise(entry.jobStatus)}`;

    default:
      return 'Recorded';
  }
}

/**
 * Who or what the entry is about, under the title. Null where there is nobody to name.
 *
 * <p>
 * A booking names the customer, because "who had it" is the question a timeline is opened to
 * answer. The other three name the person who recorded the move, which is a different fact and is
 * why they are not merged.
 * </p>
 */
export function eventWho(entry: TimelineEvent): string | null {
  return entry.source === 'Booking' ? entry.partyName : entry.byName;
}

/**
 * Whether an entry covers a span of days rather than happening at a moment.
 *
 * <p>
 * Blocks and bookings are `DateOnly` ranges — rule 14's deliberate exception, because a suit booked
 * for 12 March is booked for that calendar day at the shop. The panel renders the range for these
 * and a timestamp for the rest; showing a time of day against a block would be inventing precision
 * the record does not have.
 * </p>
 */
export function isSpan(entry: TimelineEvent): boolean {
  return entry.fromDate !== null && entry.toDate !== null;
}

/**
 * What the panel says when a track is missing because the caller may not see it.
 *
 * <p>
 * Both at once when both are withheld, rather than two notices — a person holding neither grant is
 * being told one thing about their own access, not two things about the garment.
 * </p>
 */
export function withheldNotice(timeline: {
  bookingsWithheld: boolean;
  jobsWithheld: boolean;
}): string | null {
  if (timeline.bookingsWithheld && timeline.jobsWithheld) {
    return 'The bookings and jobs this garment has been on are not shown — your role does not include them.';
  }

  if (timeline.bookingsWithheld) {
    return 'The bookings this garment has been on are not shown — your role does not include them.';
  }

  if (timeline.jobsWithheld) {
    return 'Cleaning and alteration jobs are not shown — your role does not include them.';
  }

  return null;
}

/**
 * A nullable enum name as words.
 *
 * <p>
 * A thin guard over `format/text`'s `humanizeEnum` rather than a second implementation of it — that
 * one already knows about acronyms, and two spellings of one enum on two screens is exactly the
 * drift a shared formatter exists to end. What is added here is the null, which every field on a
 * timeline entry can be.
 * </p>
 *
 * <p>
 * A missing value renders an em dash rather than "Unknown": an entry with no state is a bug in the
 * projection, and a friendly word would hide it.
 * </p>
 */
function humanise(value: string | null): string {
  return value ? humanizeEnum(value) : '—';
}
